import { apiRequest } from '../axiosInstance';
import axiosInstance from '../axiosInstance';
import { EMAIL_TEMPLATE_ENDPOINTS, buildQueryString } from '../endpoints';

const emailTemplateService = {
  getTemplates: async () => {
    return apiRequest(() =>
      axiosInstance.get(EMAIL_TEMPLATE_ENDPOINTS.LIST)
    );
  },

  getTemplateById: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(EMAIL_TEMPLATE_ENDPOINTS.GET_BY_ID(id))
    );
  },

  updateTemplate: async (id, data) => {
    return apiRequest(() =>
      axiosInstance.put(EMAIL_TEMPLATE_ENDPOINTS.UPDATE(id), data)
    );
  },

  getVariableInfo: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(EMAIL_TEMPLATE_ENDPOINTS.VARIABLE_INFO(id))
    );
  },

  getLogs: async (params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${EMAIL_TEMPLATE_ENDPOINTS.LOGS}${queryString}`)
    );
  },

  sendTestEmail: async (id, data = null) => {
    return apiRequest(() =>
      axiosInstance.post(EMAIL_TEMPLATE_ENDPOINTS.SEND_TEST(id), data)
    );
  },

  createSesTemplate: async (templateId) => {
    return apiRequest(() =>
      axiosInstance.post(EMAIL_TEMPLATE_ENDPOINTS.CREATE_SES_TEMPLATE(templateId))
    );
  },

  getSesContent: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(EMAIL_TEMPLATE_ENDPOINTS.SES_CONTENT(id))
    );
  },

  updateSesContent: async (id, data) => {
    return apiRequest(() =>
      axiosInstance.put(EMAIL_TEMPLATE_ENDPOINTS.SES_CONTENT(id), data)
    );
  },

  generateAiTemplate: async (data) => {
    return apiRequest(() =>
      axiosInstance.post(EMAIL_TEMPLATE_ENDPOINTS.GENERATE_AI, data, { timeout: 300000 })
    );
  },

  generateReferenceHtml: async (data) => {
    return apiRequest(() =>
      axiosInstance.post(EMAIL_TEMPLATE_ENDPOINTS.GENERATE_REFERENCE_HTML, data, { timeout: 300000 })
    );
  },

  getBrandingConfig: async () => {
    return apiRequest(() =>
      axiosInstance.get(EMAIL_TEMPLATE_ENDPOINTS.BRANDING_CONFIG)
    );
  },

  updateBrandingConfig: async (data) => {
    return apiRequest(() =>
      axiosInstance.put(EMAIL_TEMPLATE_ENDPOINTS.BRANDING_CONFIG, data)
    );
  },

  batchRegenerateStream: async (data, callbacks, signal) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';
    const url = `${baseUrl}${EMAIL_TEMPLATE_ENDPOINTS.BATCH_REGENERATE_STREAM}`;

    const token = localStorage.getItem('localToken');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
      signal,
    });

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Rate limit reached. Please wait a moment before trying again.');
      }
      if (response.status === 403) {
        throw new Error('You do not have permission to batch regenerate templates.');
      }
      const text = await response.text();
      throw new Error(text || `Request failed (HTTP ${response.status})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';

      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data:')) continue;

        const jsonStr = line.slice(5).trim();
        try {
          const event = JSON.parse(jsonStr);
          if (event.type === 'progress' && callbacks.onProgress) {
            callbacks.onProgress(event);
          } else if (event.type === 'done' && callbacks.onDone) {
            callbacks.onDone(event.summary);
          }
        } catch {
          // ignore individual parse errors
        }
      }
    }
  },
};

export default emailTemplateService;
